import "./App.css";
import "@mantine/core/styles.css";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "./auth/SessionContext";
import { CartModal } from "./components/Cart/CartModal";
import ProfileContent from "./components/ProfileContent/ProfileContent";
import LegalPage from "./components/Legal/LegalPage";
import LoginButton from "./components/LoginButton/LoginButton";
import { useWizard } from "./components/BuyWizard/WizardContext";
import { ActionIcon, Tooltip } from "@mantine/core";
import { IconShoppingCart } from "@tabler/icons-react";
import { BuyWizard } from "./components/BuyWizard/BuyWizard";
import {
  AppShell,
  Box,
  Burger,
  Center,
  Collapse,
  Divider,
  Drawer,
  Group,
  HoverCard,
  ScrollArea,
  UnstyledButton,
  rem,
  Menu,
  Text,
  ThemeIcon,
  SimpleGrid,
} from "@mantine/core";
import { useTranslation } from 'react-i18next';
import { useEffect  } from "react";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import Logo from "./components/Logo/Logo";
import { otherApps } from "../config/apps";
import { useDisclosure } from "@mantine/hooks";
import { animateScroll as scroll, scroller } from 'react-scroll';
import { LanguagePicker } from "./components/LanguagePicker/LanguagePicker";
import {
  IconChevronDown,
} from "@tabler/icons-react";
import classes from "./App.module.css";
import { LanguagePickerMobile } from "./components/LanguagePickerMobile/LanguagePickerMobile";
import BodyContent from "./components/BodyContent/BodyContent";
import { Edition, Duration } from "./Settings";
import { LmuTvSubscribe } from "./LmuTvSubscribe";
import { LanguageProvider } from './LanguageContext';
import { dynamicFeatureList } from "./components/FeatureCardList/FeatureCardList";
import { Download } from "./components/Download/Download";
import tvlogo from "./media/lmuTV.png";
const scrollToAnchor = (id: string) => {
  scroller.scrollTo(id, {
    duration: 500,
    delay: 0,
    offset: -64, // Offset in Pixel, um den Header zu berücksichtigen
  });
};
// Opens the buy wizard from an email/external deeplink: /lmutv/?buy=1 with optional
// &edition=pro&duration=1y&pcs=2. Strips the params afterwards so a refresh or a
// close doesn't re-open the wizard. Login is handled inside the checkout step, so a
// logged-out user can still land here and sign in there.
function DeeplinkHandler() {
  const wizard = useWizard();
  const location = useLocation();
  const navigate = useNavigate();

  // After a Steam login started from the checkout step, reopen the wizard on the
  // same tier the user was buying (stashed in sessionStorage before the redirect).
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem("lmutv_buy_intent");
      if (raw) sessionStorage.removeItem("lmutv_buy_intent");
    } catch { return; }
    if (!raw) return;
    try {
      const p = JSON.parse(raw);
      wizard?.openWizard({
        edition: p.edition === "standard" || p.edition === "pro" ? p.edition : undefined,
        duration: ["1d", "3m", "1y", "lifetime"].includes(p.duration) ? p.duration : undefined,
        pcs: typeof p.pcs === "number" && p.pcs > 0 ? Math.min(50, p.pcs) : undefined,
      });
    } catch { /* malformed stash — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("buy")) return;
    const editionRaw = params.get("edition");
    const durationRaw = params.get("duration");
    const pcsRaw = parseInt(params.get("pcs") ?? "", 10);
    const edition: Edition | undefined =
      editionRaw === "standard" || editionRaw === "pro" ? editionRaw : undefined;
    const duration: Duration | undefined =
      ["1d", "3m", "1y", "lifetime"].includes(durationRaw ?? "")
        ? (durationRaw as Duration)
        : undefined;
    const pcs =
      Number.isFinite(pcsRaw) && pcsRaw > 0 ? Math.min(50, pcsRaw) : undefined;
    wizard?.openWizard({ edition, duration, pcs });
    ["buy", "edition", "duration", "pcs"].forEach((k) => params.delete(k));
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  return null;
}

function App() {
  const { t } = useTranslation();
  const { sessionData } = useSession();
  const wizard = useWizard();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened] = useDisclosure();

  // react-scroll only scrolls within the current page. If we're on /account
  // (or any non-root route), nav links would silently do nothing — first
  // navigate to "/" then scroll once the page has mounted.
  const goToSection = (id: string) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToAnchor(id), 100);
    } else if (id === "home") {
      scroll.scrollToTop({ duration: 500 });
    } else {
      scrollToAnchor(id);
    }
  };
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [linksOpened, { toggle: toggleLinks }] = useDisclosure(false);
  const links = dynamicFeatureList.features.map((feature) => (
    <a
      onClick={() => { goToSection(feature.id); closeDrawer(); }}
      key={feature.id}>
      <UnstyledButton className={classes.subLink} key={feature.id}>
        <Group wrap="nowrap" align="flex-start">
          <ThemeIcon size={34} variant="default" radius="md">
            <feature.icon
              style={{ width: rem(22), height: rem(22) }}
              color={"#99180D"}
            />
          </ThemeIcon>
          <div>
            <Text size="sm" fw={500}>
              {i18n.t(dynamicFeatureList.title.replace("<id>", feature.id))}
            </Text>
            <Text size="xs" c="dimmed">
              {i18n.t(dynamicFeatureList.description.replace("<id>", feature.id))}
            </Text>
          </div>
        </Group>
      </UnstyledButton>
    </a>
  ));
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  });
  return (
    <LanguageProvider>
      <div className="App">
        <AppShell id="home"
          header={{ height: 60 }}
          navbar={{
            width: "100%",
            breakpoint: "sm",
            collapsed: { desktop: true, mobile: !opened },
          }}
        >
          <AppShell.Header>
            <Group h="100%" px="md" justify="space-between" style={{ display: 'flex', alignItems: 'center' }}>
              <Group style={{ flex: 1 }}>
                <Menu>
                  <Menu.Target>
                    <UnstyledButton style={{ display: 'flex', alignItems: 'center' }}>
                      <Logo />
                      <IconChevronDown size={16} style={{ marginLeft: 8 }} /> {/* Der Pfeil-Indikator */}
                    </UnstyledButton>
                  </Menu.Target>

                  <Menu.Dropdown>
                    {otherApps().map((app) => (
                      <Menu.Item key={app.name} onClick={() => window.location.href = app.url}>
                        <Logo name={app.name} logo={app.logo} />
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              </Group>
              <Burger
                opened={drawerOpened}
                onClick={toggleDrawer}
                hiddenFrom="sm"
              />
              <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                <Group  gap={10} visibleFrom="sm">
                  <UnstyledButton onClick={() => goToSection("home")}>
                    {i18n.t("menu.home")}
                  </UnstyledButton>
                  <HoverCard
                    width={600}
                    position="bottom"
                    radius="md"
                    shadow="md"
                    withinPortal
                  >
                    <HoverCard.Target>
                      <UnstyledButton onClick={() => goToSection("features")}>
                        <Center inline>
                          <Box component="span">
                            {i18n.t("menu.features")}
                          </Box>
                          <IconChevronDown
                            style={{ width: rem(16), height: rem(16) }}
                            color={"#99180D"}
                          />
                        </Center>
                      </UnstyledButton>
                    </HoverCard.Target>
                    <HoverCard.Dropdown style={{ overflow: "hidden" }}>
                      <Group justify="space-between" px="md">
                        <Text fw={500}>Features</Text>
                      </Group>
                      <Divider my="sm" />
                      <SimpleGrid cols={2} spacing={0}>
                        {links}
                      </SimpleGrid>
                    </HoverCard.Dropdown>
                  </HoverCard>
                  <UnstyledButton onClick={() => goToSection("pricing")}>
                    {i18n.t("menu.pricing")}
                  </UnstyledButton>
                </Group>
              </div>
              <Group visibleFrom="sm" gap="xs">
                <Tooltip label={t("menu.cart")}>
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    aria-label={t("menu.cart")}
                    onClick={() => wizard?.openWizard()}
                  >
                    <IconShoppingCart size={20} />
                  </ActionIcon>
                </Tooltip>
                <LanguagePicker />
                <LoginButton />
              </Group>
            </Group>
          </AppShell.Header>

          <Drawer
            opened={drawerOpened}
            onClose={closeDrawer}
            size="100%"
            padding="md"
            title={<Logo />}
            hiddenFrom="sm"
            zIndex={1000000}>
            <ScrollArea h={`calc(100vh - ${rem(80)})`} mx="-md">
              <Divider my="sm" />
              <UnstyledButton
                className={classes.link}
                onClick={() => { closeDrawer(); goToSection("home"); }}>
                <Text className={classes.link}>{i18n.t("menu.home")}</Text>
              </UnstyledButton>
              <UnstyledButton className={classes.link} onClick={toggleLinks}>
                <Center inline>
                  <Box component="span" mr={5}>
                    {i18n.t("menu.features")}
                    <IconChevronDown
                    style={{ width: rem(16), height: rem(16) }}
                    color={"#99180D"} />
                  </Box>
                </Center>
              </UnstyledButton>
              <Collapse in={linksOpened}>{links}</Collapse>
              <UnstyledButton
                className={classes.link}
                onClick={() => { closeDrawer(); goToSection("pricing"); }}>
                <Text className={classes.link}>{i18n.t("menu.pricing")}</Text>
              </UnstyledButton>
              <UnstyledButton
                className={classes.link}
                onClick={() => { closeDrawer(); wizard?.openWizard(); }}>
                <Text className={classes.link}>{t("menu.cart")}</Text>
              </UnstyledButton>
              <Divider my="sm" />
              <LoginButton />
              <Divider my="sm" />
              <LanguagePickerMobile />
            </ScrollArea>
          </Drawer>

          <AppShell.Main>
            <I18nextProvider i18n={i18n}>
              <CartModal logged_in={sessionData?.logged_in ?? false} />
              <BuyWizard />
              <DeeplinkHandler />
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <Download key="1" id="lmutvapp" index={1} dotnet="8.0.7" />
                      <Download key="2" id="lmutvappbeta" index={2} dotnet="8.0.7" />
                      <BodyContent />
                    </>
                  }
                />
                <Route path="/account" element={<ProfileContent />} />
                <Route path="/subscribe" element={<LmuTvSubscribe />} />
                <Route path="/tos" element={<LegalPage id="imprint" />} />
                <Route path="/privacy_web" element={<LegalPage id="privacy" />} />
                <Route
                  path="*"
                  element={
                    <>
                      <Download key="1" id="lmutvapp" index={1} dotnet="8.0.7" />
                      <Download key="2" id="lmutvappbeta" index={2} dotnet="8.0.7" />
                      <BodyContent />
                    </>
                  }
                />
              </Routes>
            </I18nextProvider>
          </AppShell.Main>
        </AppShell>
      </div>
    </LanguageProvider>
  );
}

export default App;
